import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MeetingForm from '../MeetingForm/MeetingForm';
import { Modal } from '../Modal/Modal';
import PendingBookingDetails from '../PendingBookingDetails/PendingBookingDetails';
import { Meeting, PendingBookingDto } from '../../api/MeetingsAPI';
import MeetingsAPI from '../../api/MeetingsAPI';
import { toastService } from '../../services/ToastService';

type Props = {
  meeting: Meeting | null;
  onMeetingChange?: (value: Meeting) => void;
  isDisabled?: boolean;
  actionButton?: JSX.Element;
  timeZone?: string;
  modalTitle?: string;
  onClose?: () => void;
};

// Remove ViewMode type since we'll use a simple boolean for modal state

function MeetingFormWrapper({ 
  meeting, 
  onMeetingChange = () => null, 
  isDisabled = false, 
  actionButton, 
  timeZone,
  modalTitle,
  onClose
}: Props) {
  const { t } = useTranslation();
  
  const [isBookingDetailsModalOpen, setIsBookingDetailsModalOpen] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<PendingBookingDto | null>(null);
  const [isLoadingBookingDetails, setIsLoadingBookingDetails] = useState(false);

  const fetchBookingDetails = async () => {
    if (!meeting?.reservationId || !meeting?.id) return;
    
    setIsLoadingBookingDetails(true);
    try {
      const response = await MeetingsAPI.getBookingByReservationId(meeting.id.toString(), meeting.reservationId);
      setBookingDetails(response.data);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      toastService.showToast(t("bookings.details.fetch_error"));
    } finally {
      setIsLoadingBookingDetails(false);
    }
  };

  const handleViewBookingDetails = () => {
    setIsBookingDetailsModalOpen(true);
    if (!bookingDetails) {
      fetchBookingDetails();
    }
  };

  const handleCloseBookingDetails = () => {
    setIsBookingDetailsModalOpen(false);
  };

  const handlePaymentStatusChange = async (id: number, paid: boolean) => {
    try {
      await MeetingsAPI.updatePaymentStatus(id, paid);
      
      if (bookingDetails) {
        setBookingDetails({ ...bookingDetails, paid });
      }
      
      await fetchBookingDetails();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toastService.showToast(t("booking.pending.payment_update_error") || "Failed to update payment status", "error");
    }
  };

  useEffect(() => {
    setBookingDetails(null);
  }, [meeting?.id, meeting?.reservationId]);

  return (
    <>
      {!isBookingDetailsModalOpen ? (
        <Modal modalTitle={modalTitle || t("calendar.meeting.form")} canClose onClose={onClose}>
          <MeetingForm
            meeting={meeting}
            onMeetingChange={onMeetingChange}
            isDisabled={isDisabled}
            actionButton={actionButton}
            timeZone={timeZone}
            onViewBookingDetails={meeting?.reservationId ? handleViewBookingDetails : undefined}
          />
        </Modal>
      ) : (
          <Modal 
             modalTitle={t("booking.details.title")} 
             canClose 
             onClose={handleCloseBookingDetails}
             >
             <PendingBookingDetails 
                 booking={bookingDetails} 
                 isLoading={isLoadingBookingDetails}
                 onPaymentStatusChange={handlePaymentStatusChange}
             />
           </Modal>
      )}
    </>
  );
}

export default MeetingFormWrapper;
